// Pure form-validation / payload-shaping logic extracted from
// AddSalesPlaybookModal.tsx so it is unit-testable without the modal UI.
import type { SalesPlaybookTag } from '../services/apiService';
import { getPlaybookNameLabel } from '../constants/labels';

export interface PlaybookRoleFormData {
  id: string;
  displayName: string;
  pitchBullets: string[];
  pitchBulletIds?: string[];
  advancedBullets: string[];
  advancedBulletIds?: string[];
  questions: { _id?: string; text: string; tags: SalesPlaybookTag[] }[];
}

export type PlaybookBulletPayload = string | { _id: string; text: string };

export interface PlaybookRolePayload {
  originalId?: string;
  displayName: string;
  pitchBullets: PlaybookBulletPayload[];
  advancedBullets: PlaybookBulletPayload[];
  questions: { _id?: string; text: string; tags: SalesPlaybookTag[] }[];
}

export type PlaybookSubmissionResult =
  | { kind: 'invalid'; error: string }
  | { kind: 'valid'; payload: { playbookDisplayName: string; roles: PlaybookRolePayload[] } };

/**
 * Validate the playbook form and shape the save payload.
 *
 * Rules (behaviour preserved from the modal):
 * - playbook name required;
 * - blank bullets/questions are stripped, roles are kept even when empty;
 * - in multiple-roles mode every role needs a name, and unnamed roles are
 *   dropped from the payload; in single-role mode the name is forced to
 *   'General';
 * - originalId is only carried for roles loaded from an existing playbook
 *   (ids other than 'general' and the local 'role-<ts>' ids).
 */
export function preparePlaybookSubmission(
  playbookDisplayName: string,
  roles: PlaybookRoleFormData[],
  useMultipleRoles: boolean
): PlaybookSubmissionResult {
  if (!playbookDisplayName.trim()) {
    return { kind: 'invalid', error: `${getPlaybookNameLabel()} is required` };
  }

  if (roles.some((role) =>
    (role.pitchBulletIds && role.pitchBulletIds.length !== role.pitchBullets.length)
    || (role.advancedBulletIds && role.advancedBulletIds.length !== role.advancedBullets.length)
  )) {
    return { kind: 'invalid', error: 'Playbook items changed unexpectedly. Reopen the editor and try again.' };
  }

  // Validate roles - keep ALL roles, only filter out those without a name in multiple roles mode
  const validRoles: PlaybookRolePayload[] = roles
    .map((role) => ({
      // Include original ID for roles loaded from existing playbook (not for newly created roles)
      originalId: role.id !== 'general' && !role.id.startsWith('role-') ? role.id : undefined,
      displayName: useMultipleRoles ? role.displayName.trim() : 'General',
      pitchBullets: role.pitchBullets.flatMap((text, index) => {
        if (!text.trim()) return [];
        const id = role.pitchBulletIds?.[index];
        return [id ? { _id: id, text } : text];
      }),
      advancedBullets: role.advancedBullets.flatMap((text, index) => {
        if (!text.trim()) return [];
        const id = role.advancedBulletIds?.[index];
        return [id ? { _id: id, text } : text];
      }),
      questions: role.questions.filter((question) => question.text.trim()),
    }))
    .filter((role) => {
      // For multiple roles mode, require a role name (but allow empty content)
      if (useMultipleRoles && !role.displayName) return false;
      // Keep all roles with a name, even if content is empty
      return true;
    });

  if (validRoles.length === 0) {
    return { kind: 'invalid', error: 'Please add at least one role' };
  }

  if (useMultipleRoles) {
    const emptyRoleNames = roles.filter((role) => !role.displayName.trim());
    if (emptyRoleNames.length > 0) {
      return { kind: 'invalid', error: 'Please provide a name for all roles' };
    }
  }

  return {
    kind: 'valid',
    payload: {
      playbookDisplayName: playbookDisplayName.trim(),
      roles: validRoles,
    },
  };
}
